import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  type PayrollFilesRequest,
  type PayrollMonthItem,
  type PayrollPreviewResponse,
  salaryPeriodLabel,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { apiRequest } from '../../lib/api.js';
import { formatHours, peopleKeys } from './people-ui.js';

/** The content of a file in base64, as the API wants it. */
function base64Of(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(new Error(`«${file.name}» non si legge`));
    reader.readAsDataURL(file);
  });
}

const CHECK_ICONS = { ok: '✓', warn: '⚠', error: '✕' } as const;

/**
 * «Carica le paghe del mese…» (2.37): the PDFs of the consultant are read by the
 * API, shown with their checks, and saved only after the confirmation. A month
 * already loaded is replaced (errors of the consultant are corrected so).
 */
export function PayrollLoadDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (saved: PayrollMonthItem) => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [body, setBody] = useState<PayrollFilesRequest | null>(null);
  const [readError, setReadError] = useState('');
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const preview = useMutation({
    mutationFn: (request: PayrollFilesRequest) =>
      apiRequest<PayrollPreviewResponse>('/people/payroll/preview', {
        method: 'POST',
        body: request,
      }),
  });
  const save = useMutation({
    mutationFn: (request: PayrollFilesRequest) =>
      apiRequest<PayrollMonthItem>('/people/payroll', { method: 'POST', body: request }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: peopleKeys.all });
      onSaved(saved);
      onClose();
    },
  });
  const choose = async (list: FileList | null) => {
    setReadError('');
    preview.reset();
    save.reset();
    if (!list?.length) return;
    try {
      const files = await Promise.all(
        [...list].map(async (file) => ({ name: file.name, content: await base64Of(file) })),
      );
      setBody({ files });
      preview.mutate({ files });
    } catch (error) {
      setReadError(error instanceof Error ? error.message : 'I file non si leggono');
    }
  };
  const data = preview.data;

  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <div className="stack">
        <h2>
          {data
            ? `Paghe di ${salaryPeriodLabel(data.year, data.month).toLowerCase()}`
            : 'Carica le paghe del mese'}
        </h2>
        <p className="muted">
          Scegli insieme i PDF del consulente: i cedolini («Dipendenti») e il riepilogo del mese
          («Riepiloghi»). Si può aggiungere qualsiasi altro PDF del mese (prospetto contabile, F24,
          distinta dei netti): viene solo archiviato. Per ora si legge il formato in uso dal 2026.
        </p>
        <input
          type="file"
          accept="application/pdf,.pdf"
          multiple
          onChange={(event) => void choose(event.target.files)}
        />
        {readError && <p className="error">{readError}</p>}
        {preview.isPending && <p className="muted">Lettura dei PDF…</p>}
        {preview.isError && <p className="error">{preview.error.message}</p>}
        {data && (
          <>
            {data.replaces && (
              <p className="notice info">
                {salaryPeriodLabel(data.year, data.month)} è già caricato: salvando lo sostituisci
                (resta traccia nel registro attività).
              </p>
            )}
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Persona</th>
                    <th className="numeric">Imponibile INPS</th>
                    <th className="numeric">di cui fringe auto</th>
                    <th className="numeric">INPS azienda</th>
                    <th className="numeric">TFR</th>
                    <th className="numeric">Buoni pasto</th>
                    <th className="numeric">Welfare</th>
                    <th className="numeric">Netto</th>
                    <th className="numeric">Ore lavorate</th>
                    <th className="numeric">Straord.</th>
                    <th className="numeric">Ferie / ROL / fest.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.slips.map((slip) => (
                    <tr key={slip.taxCode}>
                      <td>
                        {slip.employeeName ?? <span className="negative">{slip.name}</span>}
                        {slip.thirteenth && <span className="pill info"> 13ª</span>}
                      </td>
                      <td className="numeric">{formatMoney(slip.inpsBase)}</td>
                      <td className="numeric">
                        {Number(slip.fringeCar) ? formatMoney(slip.fringeCar) : '—'}
                      </td>
                      <td className="numeric">{formatMoney(slip.inpsCompany)}</td>
                      <td className="numeric">
                        {formatMoney(slip.tfrQuota)}{' '}
                        <small className="muted">{slip.tfrToFund ? 'fondo' : 'azienda'}</small>
                      </td>
                      <td className="numeric">{formatMoney(slip.mealVouchers)}</td>
                      <td className="numeric">
                        {Number(slip.welfare) ? formatMoney(slip.welfare) : '—'}
                      </td>
                      <td className="numeric">
                        {formatMoney(slip.net)}
                        {slip.previousNet !== null && (
                          <small className="muted"> prima {formatMoney(slip.previousNet)}</small>
                        )}
                      </td>
                      <td className="numeric">{formatHours(slip.hoursWorked)}</td>
                      <td className="numeric">
                        {Number(slip.overtimeHours) ? formatHours(slip.overtimeHours) : '—'}
                      </td>
                      <td className="numeric">
                        {formatHours(slip.leaveHours)} / {formatHours(slip.rolHours)} /{' '}
                        {formatHours(slip.holidayHours)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="plain-list">
              {data.checks.map((check, index) => (
                <li
                  key={index}
                  className={
                    check.level === 'ok'
                      ? undefined
                      : check.level === 'warn'
                        ? 'warn-text'
                        : 'negative'
                  }
                >
                  {CHECK_ICONS[check.level]} {check.message}
                </li>
              ))}
            </ul>
          </>
        )}
        {save.isError && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!data?.canSave || !body || save.isPending}
            onClick={() => body && save.mutate(body)}
          >
            {save.isPending
              ? 'Salvataggio…'
              : data?.replaces
                ? 'Sostituisci il mese'
                : 'Conferma e salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
